import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, MapPin, Navigation, Loader2, Check, AlertCircle, AlertTriangle, Radio } from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";

// Map component that tracks center position
function MapTracker({ onCenterChange }) {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      onCenterChange(center.lat, center.lng);
    },
    move: () => {
      const center = map.getCenter();
      onCenterChange(center.lat, center.lng);
    }
  });
  return null;
}

// Component to set map view
function SetMapView({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
}

export default function EditPage({ objectId, objectData, onSave, onBack }) {
  // Extract existing data from description.location.coords
  const locationData = objectData?.description?.location || {};
  const coords = locationData.coords || {};
  const existingLat = coords.latitude || null;
  const existingLng = coords.longitude || null;

  const rawSensorType = objectData?.description?.sensorType;
  const lockedSensorType = rawSensorType === "alarm" || rawSensorType === "tracker" ? rawSensorType : null;
  const [name, setName] = useState(objectData?.title || "");
  const [sensorType, setSensorType] = useState(lockedSensorType);
  const [latitude, setLatitude] = useState(existingLat || "");
  const [longitude, setLongitude] = useState(existingLng || "");
  const [isLocating, setIsLocating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMap, setShowMap] = useState(existingLat && existingLng ? true : false);
  const [mapCenter, setMapCenter] = useState(
    existingLat && existingLng 
      ? [existingLat, existingLng] 
      : [51.1657, 10.4515] // Default: Germany center
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMapCenterChange = (lat, lng) => {
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      setGpsError("Geolocation wird nicht unterstützt");
      toast.error("Geolocation nicht verfügbar");
      return;
    }

    setIsLocating(true);
    setGpsError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setLatitude(lat.toFixed(6));
        setLongitude(lng.toFixed(6));
        setMapCenter([lat, lng]);
        setShowMap(true);
        setIsLocating(false);
        toast.success("Standort ermittelt");
      },
      (error) => {
        setIsLocating(false);
        let message = "Standort konnte nicht ermittelt werden";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = "Standort-Zugriff verweigert";
            break;
          case error.POSITION_UNAVAILABLE:
            message = "Standort nicht verfügbar";
            break;
          case error.TIMEOUT:
            message = "Zeitüberschreitung";
            break;
        }
        
        setGpsError(message);
        toast.error(message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Bitte gib eine Bezeichnung ein");
      return;
    }

    const effectiveType = lockedSensorType || sensorType;
    if (!effectiveType) {
      toast.error("Bitte wähle einen Typ (Alarmpunkt oder Tracker)");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        qr_id: objectId, // Full ID with IoT:
        name: name.trim(),
        sensor_type: effectiveType,
        latitude: latitude ? parseFloat(latitude) : null,
        longitude: longitude ? parseFloat(longitude) : null,
        perm_id: objectData?.["perm-id"] || objectData?.permId
      };

      const response = await api.put("/objects/update", payload);

      if (response.data.success) {
        const checkResponse = await api.get(`/objects/${encodeURIComponent(objectId)}/check`);

        if (navigator.vibrate) {
          navigator.vibrate([50, 50, 100]);
        }

        onSave(checkResponse.data.data);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Fehler beim Speichern", {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Display ID without IoT: prefix
  const displayId = objectId?.replace("IoT:", "") || "";

  return (
    <div className="min-h-[100dvh] bg-[var(--sa-light)]">
      {/* Fixed Header with scroll shadow */}
      <header className={`fixed top-0 left-0 right-0 z-20 bg-white transition-shadow duration-200 ${isScrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="flex items-center gap-4 px-4 py-3 safe-area-top">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-[var(--sa-secondary)]" />
          </Button>
          <div className="flex-1 min-w-0">
            <img 
              src="/safety-assist-logo.png" 
              alt="SafetyAssist" 
              className="h-8 object-contain"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="pt-20 pb-52 px-4 safe-area-bottom animate-slide-up">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[var(--sa-secondary)]">
              Objekt bearbeiten
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              ID: <span className="font-mono">{displayId}</span>
            </p>
          </div>

          {/* Name Input */}
          <Card className="p-5 bg-white shadow-sm border-0 rounded-xl">
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-[var(--sa-secondary)]">
                Bezeichnung *
              </Label>
              <Input
                data-testid="name-input"
                type="text"
                placeholder="z.B. Feuerlöscher Halle A"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 bg-[var(--sa-light)] border-0 rounded-lg focus:ring-2 focus:ring-[var(--sa-primary)]"
              />

              {/* Sensor Type */}
              <Label className="text-sm font-semibold text-[var(--sa-secondary)] pt-1">
                Typ *
              </Label>
              {lockedSensorType ? (
                <div
                  data-testid="sensor-type-locked"
                  className="flex items-center gap-2 h-12 px-4 rounded-lg bg-[var(--sa-light)] text-[var(--sa-secondary)] font-medium text-sm"
                >
                  {lockedSensorType === "alarm" ? (
                    <AlertTriangle className="w-4 h-4 text-[var(--sa-primary)]" />
                  ) : (
                    <Radio className="w-4 h-4 text-[var(--sa-primary)]" />
                  )}
                  {lockedSensorType === "alarm" ? "Alarmpunkt" : "Tracker"}
                  <span className="ml-auto text-xs text-gray-400">festgelegt</span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="sensor-type-alarm-btn"
                    onClick={() => setSensorType("alarm")}
                    className={`flex items-center justify-center gap-2 h-12 rounded-lg font-medium text-sm transition-colors ${
                      sensorType === "alarm"
                        ? "bg-[var(--sa-primary)] text-white"
                        : "bg-[var(--sa-light)] text-[var(--sa-secondary)]"
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Alarmpunkt
                  </button>
                  <button
                    type="button"
                    data-testid="sensor-type-tracker-btn"
                    onClick={() => setSensorType("tracker")}
                    className={`flex items-center justify-center gap-2 h-12 rounded-lg font-medium text-sm transition-colors ${
                      sensorType === "tracker"
                        ? "bg-[var(--sa-primary)] text-white"
                        : "bg-[var(--sa-light)] text-[var(--sa-secondary)]"
                    }`}
                  >
                    <Radio className="w-4 h-4" />
                    Tracker
                  </button>
                </div>
              )}
            </div>
          </Card>

          {/* GPS Section */}
          <Card className="p-5 bg-white shadow-sm border-0 rounded-xl">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-[var(--sa-secondary)]">
                  GPS-Standort (optional)
                </Label>
                <Button
                  data-testid="get-location-btn"
                  variant="outline"
                  size="sm"
                  onClick={getLocation}
                  disabled={isLocating}
                  className="h-9 text-sm font-medium border-[var(--sa-primary)] text-[var(--sa-primary)] hover:bg-[var(--sa-primary)]/10"
                >
                  {isLocating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Ermittle...
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4 mr-2" />
                      Automatisch
                    </>
                  )}
                </Button>
              </div>

              {gpsError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {gpsError}
                </div>
              )}

              {!showMap && !gpsError && (
                <div className="p-6 bg-[var(--sa-light)] rounded-xl text-center">
                  <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">
                    Klicke auf "Automatisch" um deinen Standort zu ermitteln
                  </p>
                </div>
              )}

              {/* Map - only shown after location is set */}
              {showMap && (
                <>
                  <div className="map-container relative rounded-xl overflow-hidden border border-gray-200">
                    <MapContainer
                      center={mapCenter}
                      zoom={15}
                      style={{ height: "250px", width: "100%" }}
                      zoomControl={false}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <MapTracker onCenterChange={handleMapCenterChange} />
                      <SetMapView center={mapCenter} />
                    </MapContainer>
                    
                    {/* Center Pin */}
                    <div className="center-pin">
                      <MapPin className="w-10 h-10 text-[var(--sa-primary)] drop-shadow-lg" fill="var(--sa-primary)" />
                    </div>
                    
                    {/* Instruction */}
                    <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                      <p className="text-xs text-gray-600">
                        Bewege die Karte, um den Standort zu setzen
                      </p>
                    </div>
                  </div>

                  {/* Coordinates Display */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[var(--sa-light)] rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Breitengrad</p>
                      <p className="font-mono text-sm text-[var(--sa-secondary)]" data-testid="latitude-display">
                        {latitude || "—"}
                      </p>
                    </div>
                    <div className="p-3 bg-[var(--sa-light)] rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Längengrad</p>
                      <p className="font-mono text-sm text-[var(--sa-secondary)]" data-testid="longitude-display">
                        {longitude || "—"}
                      </p>
                    </div>
                  </div>

                  {latitude && longitude && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 text-sm rounded-lg">
                      <Check className="w-4 h-4 shrink-0" />
                      Standort gesetzt
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Save Button */}
          <Button
            data-testid="save-btn"
            onClick={handleSave}
            disabled={isSaving || !name.trim() || (!lockedSensorType && !sensorType)}
            className="w-full h-14 bg-[var(--sa-primary)] hover:bg-[var(--sa-primary-dark)] text-white rounded-xl font-semibold text-base btn-press"
          >
            {isSaving ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Speichern...</span>
              </div>
            ) : (
              "Speichern"
            )}
          </Button>
        </div>
      </main>
    </div>
  );
}
